import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setAuth } from '../auth';
import { PageContainer } from '../components/PageContainer';

export function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? 'Login failed');
        return;
      }

      const { token, username: user } = await res.json();
      setAuth(token, user);
      navigate('/', { replace: true });
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageContainer>
    <div style={styles.outer}>
      <div style={styles.card}>
        <div style={styles.wordmark}>POSTERNAUTS AGENT</div>
        <p style={styles.subtitle}>Mission Control · Restricted Access</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={styles.input}
              disabled={loading}
              spellCheck={false}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            disabled={loading || !username || !password}
            style={{
              ...styles.button,
              opacity: loading || !username || !password ? 0.45 : 1,
              cursor: loading || !username || !password ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
    </PageContainer>
  );
}

const styles: Record<string, React.CSSProperties> = {
  outer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0c0c12',
    padding: '2rem',
  },
  card: {
    width: '100%',
    maxWidth: '360px',
    background: '#111118',
    border: '1px solid #222230',
    borderRadius: '12px',
    padding: '2.5rem',
  },
  wordmark: {
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#a0a8ff',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#444',
    letterSpacing: '0.04em',
    marginBottom: '2rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#888',
    letterSpacing: '0.04em',
  },
  input: {
    background: '#0c0c12',
    border: '1px solid #2a2a3a',
    borderRadius: '6px',
    padding: '9px 12px',
    fontSize: '14px',
    color: '#e8e8f0',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  error: {
    fontSize: '13px',
    color: '#ff6b6b',
    margin: 0,
  },
  button: {
    marginTop: '0.25rem',
    background: '#a0a8ff',
    color: '#0c0c12',
    border: 'none',
    borderRadius: '6px',
    padding: '10px',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    transition: 'opacity 0.15s',
  },
};